import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INSPIRATION_TAGS = [
  "Tienda online", "Reservas", "SEO", "Multidioma", "Área privada",
  "Integraciones", "Animaciones", "Blog", "Otro",
];

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Subtle synthesized "seal" sound via Web Audio — no external file.
function playSeal(muted) {
  if (muted) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    // low thud
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(150, now);
    o.frequency.exponentialRampToValueAtTime(60, now + 0.14);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.22, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    o.connect(g).connect(ctx.destination);
    o.start(now);
    o.stop(now + 0.24);
    // soft paper click
    const bufferSize = ctx.sampleRate * 0.05;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    const noise = ctx.createBufferSource();
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.08, now);
    ng.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
    noise.buffer = buffer;
    noise.connect(ng).connect(ctx.destination);
    noise.start(now);
    setTimeout(() => ctx.close(), 400);
  } catch (e) { /* ignore audio errors */ }
}

// Ear-border tribute (kept from prior refinement)
function EarBorder() {
  return (
    <div className="absolute -top-[22px] left-0 right-0 h-6 pointer-events-none select-none">
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: "rgba(250,248,245,0.28)" }} />
      <svg className="absolute bottom-0 left-[30%]" width="44" height="24" viewBox="0 0 44 24" fill="none" aria-hidden="true">
        <path d="M 2 23 L 22 2 L 42 23" stroke="rgba(250,248,245,0.5)" strokeWidth="1.2" strokeLinejoin="round" fill="none" />
      </svg>
      <svg className="absolute bottom-0 left-[44%]" width="44" height="24" viewBox="0 0 44 24" fill="none" aria-hidden="true">
        <path d="M 2 23 L 22 2 L 42 23" stroke="rgba(250,248,245,0.5)" strokeWidth="1.2" strokeLinejoin="round" fill="none" />
      </svg>
    </div>
  );
}

// The travelling cat for the confirmation sequence.
function TravellingCat() {
  return (
    <svg viewBox="0 0 120 90" width="120" height="90" aria-hidden="true">
      {/* body */}
      <path d="M 30 78 C 24 58, 26 44, 40 40 C 38 30, 44 22, 54 22 C 50 14, 52 6, 60 4 C 68 6, 70 14, 66 22 C 76 22, 82 30, 80 40 C 94 44, 96 58, 90 78 Z" fill="#111" />
      {/* ears inner */}
      <path d="M 52 12 L 56 22 L 50 18 Z" fill="#3a2620" />
      <path d="M 68 12 L 64 22 L 70 18 Z" fill="#3a2620" />
      {/* tail */}
      <path d="M 90 66 C 108 62, 114 48, 104 40" stroke="#111" strokeWidth="6" fill="none" strokeLinecap="round" />
      {/* suitcase */}
      <rect x="14" y="58" width="22" height="16" rx="2" fill="#F2EFEB" stroke="#111" strokeWidth="1.4" />
      <rect x="22" y="54" width="7" height="5" rx="1" fill="none" stroke="#111" strokeWidth="1.2" />
      <rect x="16" y="63" width="18" height="2" fill="#FF6B2C" />
    </svg>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ nombre: "", empresa: "", email: "", telefono: "", mensaje: "" });
  const [stage, setStage] = useState("idle"); // idle | seal | wait | cat | success
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState("");
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const runSequence = () => {
    const reduced = prefersReducedMotion();
    if (reduced) { setStage("success"); return; }
    playSeal(muted);
    setStage("seal");
    timers.current.push(setTimeout(() => setStage("wait"), 500));
    timers.current.push(setTimeout(() => setStage("cat"), 2200));
    timers.current.push(setTimeout(() => setStage("success"), 7000));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nombre || !form.email || !form.mensaje) {
      setError("Nombre, email y mensaje son obligatorios.");
      return;
    }
    // Start the cinematic sequence immediately (does not block on network)
    runSequence();
    try {
      await axios.post(`${API}/leads`, form);
    } catch (err) {
      // Confirmation is not gated on the request per spec; log silently.
      // eslint-disable-next-line no-console
      console.error("lead submit error", err?.response?.data?.detail || err.message);
    }
  };

  const busy = stage !== "idle";

  return (
    <section id="contact" data-testid="contact-section" className="relative section-dark py-16 lg:py-24">
      <div className="max-w-[95%] mx-auto">
        <div className="grid grid-cols-12 gap-8 lg:gap-16">
          {/* Left */}
          <div className="col-span-12 lg:col-span-5">
            <div className="font-sans text-[11px] tracking-[0.24em] uppercase opacity-60 mb-6">— Contacto · Índice 007</div>
            <h2 className="font-display text-[14vw] sm:text-[10vw] lg:text-[7vw] leading-[0.95] tightest pb-2">
              ¿Hablamos<span className="font-italic-display" style={{ color: "#FF6B2C" }}>?</span>
            </h2>
            <p className="mt-8 font-sans text-lg leading-relaxed opacity-80 max-w-md">
              Cuéntanos tu proyecto y te responderemos en menos de un día laborable. Sin formularios eternos.
            </p>
            <div className="mt-14 space-y-6">
              <div>
                <div className="font-sans text-[11px] tracking-[0.22em] uppercase opacity-50">Email</div>
                <a href="mailto:hello@lamichiagencia.com" className="font-display text-2xl lg:text-3xl link-underline">
                  hello@lamichiagencia.com
                </a>
              </div>
              <div>
                <div className="font-sans text-[11px] tracking-[0.22em] uppercase opacity-50">Estudio</div>
                <div className="font-display text-2xl lg:text-3xl">Barcelona, España</div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="col-span-12 lg:col-span-7 lg:pl-8"
          >
            <div className="relative form-frame pt-14">
              <EarBorder />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-2">
                <Field label="Nombre" name="nombre" value={form.nombre} onChange={onChange} placeholder="María López" testId="input-nombre" />
                <Field label="Empresa" name="empresa" value={form.empresa} onChange={onChange} placeholder="Estudio Balma" testId="input-empresa" />
                <Field label="Email" type="email" name="email" value={form.email} onChange={onChange} placeholder="hola@tuempresa.com" testId="input-email" />
                <Field label="Teléfono" name="telefono" value={form.telefono} onChange={onChange} placeholder="+34 600 000 000" testId="input-telefono" />
              </div>

              <div className="mt-10">
                <label className="block font-sans text-[11px] tracking-[0.22em] uppercase opacity-50 mb-3">
                  Puedes contarnos cosas como:
                </label>
                <textarea
                  data-testid="input-mensaje"
                  name="mensaje"
                  value={form.mensaje}
                  onChange={onChange}
                  rows={5}
                  placeholder="¿Qué tienes en mente? Cuéntanos tu idea, qué objetivos tienes, qué funcionalidades necesitas o comparte cualquier referencia que te inspire."
                  className="editorial-input resize-none !text-[#FAF8F5]"
                  style={{ borderBottomColor: "rgba(250,248,245,0.2)" }}
                />
                <div className="mt-4 flex flex-wrap gap-2" aria-hidden="true" data-testid="inspiration-tags">
                  {INSPIRATION_TAGS.map((t) => (
                    <span key={t} className="inspiration-tag">{t}</span>
                  ))}
                </div>
              </div>

              {error && (
                <div data-testid="contact-error" className="mt-6 font-sans text-sm" style={{ color: "#FF6B2C" }}>{error}</div>
              )}

              <div className="mt-12 flex flex-wrap items-center justify-between gap-6">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={busy}
                  className="relative inline-flex items-center gap-3 pl-7 pr-6 py-4 rounded-full overflow-visible group disabled:opacity-70"
                  style={{ background: "#FF6B2C", color: "#111", border: "1px solid #FF6B2C" }}
                >
                  <span className="relative z-[1] font-sans text-[14px] tracking-[0.02em]">Comenzar el proyecto</span>
                  <svg className="relative z-[1] transition-transform duration-500 group-hover:translate-x-1" width="16" height="14" viewBox="0 0 16 14" fill="none" aria-hidden="true">
                    <path d="M1 7h13M9 2l5 5-5 5" stroke="#111" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <span className="font-sans text-[11px] tracking-[0.22em] uppercase opacity-60 max-w-xs">
                  Al enviar, aceptas nuestros términos y condiciones. Nunca compartimos tus datos.
                </span>
              </div>
            </div>
          </motion.form>
        </div>
      </div>

      {/* --- Cinematic confirmation overlay --- */}
      <AnimatePresence>
        {busy && (
          <motion.div
            key="confirm-overlay"
            data-testid="confirmation-overlay"
            className="fixed inset-0 z-[9600] flex items-center justify-center overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            style={{ background: "rgba(17,17,17,0.96)", color: "#FAF8F5" }}
          >
            {/* mute toggle */}
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              data-testid="confirmation-mute"
              className="absolute top-6 right-6 font-sans text-[11px] tracking-[0.2em] uppercase opacity-60 hover:opacity-100"
            >
              {muted ? "Sonido: off" : "Sonido: on"}
            </button>

            {/* Stamp */}
            {stage === "seal" && (
              <motion.svg
                width="220" height="120" viewBox="0 0 220 120" fill="none" aria-hidden="true"
                initial={{ scale: 0.4, opacity: 0, rotate: -16 }}
                animate={{ scale: [0.4, 1.15, 1], opacity: 1, rotate: -8 }}
                transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
              >
                <rect x="6" y="6" width="208" height="108" rx="10" stroke="#FF6B2C" strokeWidth="2" />
                <rect x="12" y="12" width="196" height="96" rx="8" stroke="#FF6B2C" strokeWidth="1.4" strokeDasharray="4 4" />
                <circle cx="110" cy="60" r="26" stroke="#FF6B2C" strokeWidth="1.6" />
                <path d="M92 60 L110 44 L128 60 L110 76 Z" stroke="#FF6B2C" strokeWidth="1.4" fill="none" />
              </motion.svg>
            )}

            {/* Wait */}
            {stage === "wait" && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center px-6">
                <div className="flex items-center justify-center gap-1.5 mb-6">
                  {[0, 1, 2].map((i) => (
                    <motion.span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: "#FF6B2C" }}
                      animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }} />
                  ))}
                </div>
                <div className="font-display text-3xl lg:text-5xl leading-[1.05] tighter pb-1">Preparando el siguiente destino…</div>
              </motion.div>
            )}

            {/* Cat walking */}
            {stage === "cat" && (
              <>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.5 }} className="absolute top-[32%] left-0 right-0 text-center font-display text-3xl lg:text-5xl leading-[1.05] tighter pb-1 px-6">
                  Preparando el siguiente destino…
                </motion.div>
                <motion.div
                  className="absolute bottom-[18%]"
                  initial={{ left: "-14%" }}
                  animate={{ left: ["-14%", "44%", "48%", "114%"] }}
                  transition={{ duration: 4.6, times: [0, 0.42, 0.6, 1], ease: "easeInOut" }}
                >
                  {/* subtle walking bob */}
                  <motion.div animate={{ y: [0, -4, 0] }} transition={{ duration: 0.5, repeat: Infinity }}>
                    <TravellingCat />
                  </motion.div>
                </motion.div>
              </>
            )}

            {/* Success */}
            {stage === "success" && (
              <motion.div
                data-testid="confirmation-success"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
                className="text-center px-6 max-w-2xl"
              >
                <div className="font-italic-display text-[13vw] lg:text-[7vw] leading-none tightest pb-2" style={{ color: "#FF6B2C" }}>
                  ¡Todo listo!
                </div>
                <p className="mt-8 font-display text-2xl lg:text-3xl leading-snug tighter pb-1">
                  Hemos recibido tu mensaje.
                </p>
                <p className="mt-4 font-sans text-base lg:text-lg leading-relaxed opacity-80">
                  Prepararemos el siguiente destino y nos pondremos en contacto contigo muy pronto.
                </p>
                <button
                  type="button"
                  onClick={() => { setForm({ nombre: "", empresa: "", email: "", telefono: "", mensaje: "" }); setStage("idle"); }}
                  data-testid="confirmation-close"
                  className="btn-ghost mt-12 !border-white/25 !text-[#FAF8F5]"
                >
                  Volver
                </button>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <label className="block font-sans text-[11px] tracking-[0.22em] uppercase opacity-50 pt-6">{label}</label>
      <input
        data-testid={testId}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="editorial-input !text-[#FAF8F5]"
        style={{ borderBottomColor: "rgba(250,248,245,0.2)" }}
      />
    </div>
  );
}
